import { Component, OnInit, ChangeDetectionStrategy, signal } from "@angular/core";
import { CommonModule } from "@angular/common";
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule } from "@angular/forms";
import { ActivatedRoute, Router } from "@angular/router";
import { Charge, ChargeStatus } from "../models/charge.models";
import { CurrencyClpPipe } from "../pipes/currency-clp.pipe";
import { BadgeComponent } from "../ui/badge/badge.component";
import { ButtonComponent } from "../ui/button/button.component";
import { NewChargeDialogComponent } from "../new-charge-dialog/new-charge-dialog.component";
import { StatusDialogComponent } from "../status-dialog/status-dialog.component";
import { ChargeService, getBadgeVariant, OnlinecheckoutLineItem } from "../../../services/graphQl/charge/charge.service";
import { OnlineCheckoutService } from "../../../services/online-checkout.service";
import { debounceTime, distinctUntilChanged, Subject, takeUntil } from "rxjs";
import { SortDirection } from "../../../models/common";
import { DataViewModule } from "primeng/dataview";
import { NgxSkeletonLoaderModule } from "ngx-skeleton-loader";
import { SplitButtonModule } from "primeng/splitbutton";
import { MenuItem } from "primeng/api";
import { ChargeDetailDialogComponent } from "../charge-detail-dialog/charge-detail-dialog.component";
import { ConfirmChargeDialogComponent } from "../confirm-charge-dialog/confirm-charge-dialog.component";
import { ExportService } from "../services/export.service";
import { DrawerModule } from "primeng/drawer";
import { RadioButtonModule } from "primeng/radiobutton";
import { AccordionModule } from "primeng/accordion";
import { CheckboxModule } from "primeng/checkbox";
import { DatePickerModule } from "primeng/datepicker";

import { DividerModule } from "primeng/divider";

@Component({
  selector: "app-charges-list",
  standalone: true,
  imports: [FormsModule, ReactiveFormsModule, CommonModule, CurrencyClpPipe, BadgeComponent, NewChargeDialogComponent, StatusDialogComponent, DataViewModule, NgxSkeletonLoaderModule, SplitButtonModule, ChargeDetailDialogComponent, ConfirmChargeDialogComponent, DrawerModule, RadioButtonModule, CheckboxModule, DatePickerModule, DividerModule, AccordionModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: "./charges-list.component.html",
  styleUrls: ["./charges-list.component.scss"],
})
export class ChargesListComponent implements OnInit {
  private destroy$ = new Subject<void>();

  charges = signal<Charge[]>([]);
  filteredCharges = signal<Charge[]>([]);
  loading = signal(true);
  searchTerm = signal("");
  showNewChargeDialog = signal(false);
  showDetailChargeDialog = signal(false);
  showConfirmChargeDialog = signal(false);
  showStatusDialog = signal<"success" | "error" | null>(null);
  selectedCharge = signal<Charge | null>(null);
  pageSize = signal(10);
  currentPage = signal(1);
  exporting = signal(false);

  exportItems: MenuItem[] = [
    { label: "Exportar CSV", icon: "pi pi-file", command: () => this.export("csv") },
    { label: "Exportar Excel", icon: "pi pi-file-excel", command: () => this.export("xlsx") },
  ];

  filterControl = new FormControl("");
  visible = false;
  form!: FormGroup;
  availableTypes: string[] = [];
  statusOptions = Object.values(ChargeStatus);

  sortField: keyof Charge | undefined = undefined;
  sortDirection: SortDirection | undefined = undefined;

  sortDirectionEnum = SortDirection;
  ChargeStatusEnum = ChargeStatus;

  loadingSkeletonRows: any[] = [{}, {}, {}, {}, {}, {}, {}, {}, {}, {}];

  constructor(private route: ActivatedRoute, private router: Router, private chargeService: ChargeService, private onlineCheckoutService: OnlineCheckoutService, private exportService: ExportService) {}

  export(type: "csv" | "xlsx") {
    if (this.exporting()) return;

    this.exporting.set(true);

    try {
      const charges = this.filteredCharges();
      const filename = this.exportService.generateFilename(type);
      const formatDate = this.exportService.formatDateForPortal.bind(this.exportService);

      const meta = {
        portalName: "Portal de Comercio — Cobros",
        exportedAt: new Date(),
        filtersSummary: this.searchTerm() ? `Filtro aplicado: "${this.searchTerm()}"` : undefined,
      };

      if (type === "csv") {
        const csvContent = this.exportService.buildChargesCsv(charges, formatDate, meta);
        this.exportService.downloadCsv(csvContent, filename);
      } else {
        const workbook = this.exportService.buildChargesWorkbook(charges, formatDate, meta);
        this.exportService.downloadXlsx(workbook, filename);
      }
    } catch (error) {
      console.error("Error durante la exportación:", error);
    } finally {
      this.exporting.set(false);
    }
  }

  ngOnInit(): void {
    this.loadCharges();
    console.log("AuthComponent ngOnInit");

    this.filterControl.valueChanges
      .pipe(
        debounceTime(500), // espera 500ms
        distinctUntilChanged() // evita repeticiones
      )
      .subscribe((inputValue) => {
        this.onSearch(inputValue);
      });

    this.route.queryParams.subscribe((params: any) => {
      console.log("params", params);

      if (!params || Object.keys(params).length === 0) {
        return;
      }

      const jwt = params["jwt"];
      if (jwt && jwt !== "undefined") {
        try {
          const payload = JSON.parse(atob(jwt.split(".")[1]));
          if (payload.paymentIntentId) {
            this.showStatusDialog.set("success");
          } else {
            this.showStatusDialog.set("error");
          }
        } catch (error) {
          console.error("Error parsing JWT:", error);
        }
        this.router.navigate(["/charge"]);
      }
    });

    this.chargeService.loading$.pipe(takeUntil(this.destroy$)).subscribe((loading) => {
      console.log("loading service", loading);
      this.loading.set(loading);
    });
  }

  loadCharges(): void {
    this.loading.set(true);

    this.chargeService
      .listAllCharges()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (charges) => {
          console.log("Raw charges from service:", charges);
          const mappedCharges: Charge[] = charges.map((charge: any) => ({
            ...charge,
            menuItems: this.getMenuItems(charge),
          }));
          console.log("Mapped charges:", mappedCharges);
          const sortedCharges = mappedCharges.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
          this.charges.set(sortedCharges);
          this.filteredCharges.set(sortedCharges);

          // Actualizar tipos disponibles si el formulario ya existe
          if (this.form) {
            this.availableTypes = [...new Set(sortedCharges.map((c) => c.type))];
          }
        },
        error: (error) => {
          console.error("Error loading charges:", error);
        },
      });
  }

  onSearch(term: string): void {
    this.searchTerm.set(term);
    this.applyAllFilters();
  }

  openNewChargeDialog(): void {
    this.showNewChargeDialog.set(true);
  }

  closeNewChargeDialog(): void {
    this.showNewChargeDialog.set(false);
    this.selectedCharge.set(null);
    this.loadCharges();
  }

  closeNewChargeDialogBlank(): void {
    this.showNewChargeDialog.set(false);
    this.selectedCharge.set(null);
  }

  onChargeCreated(): void {
    this.closeNewChargeDialog();
    this.loadCharges();
  }

  closeStatusDialog(): void {
    this.showStatusDialog.set(null);
  }

  onStatusClick(charge: Charge): void {
    console.log("charge => ", charge);
    this.selectedCharge.set(charge);
    this.showNewChargeDialog.set(true);
  }

  get paginatedCharges(): Charge[] {
    const start = (this.currentPage() - 1) * this.pageSize();
    const end = start + this.pageSize();
    return this.filteredCharges().slice(start, end);
  }

  get totalPages(): number {
    return Math.ceil(this.filteredCharges().length / this.pageSize());
  }

  sort(field: keyof Charge) {
    this.sortField = field;
    switch (this.sortDirection) {
      case SortDirection.ASC:
        this.sortDirection = SortDirection.DESC;
        break;
      case SortDirection.DESC:
        this.sortDirection = undefined;
        this.sortField = undefined;
        break;
      default:
        this.sortDirection = SortDirection.ASC;
        break;
    }
    this.applySorting();
  }

  isSortedBy(field: keyof Charge, direction: SortDirection): boolean {
    return this.sortField === field && this.sortDirection === direction;
  }

  private getMenuItems(charge: Charge): MenuItem[] {
    console.log("getMenuItems charge", charge);
    if (charge.status === ChargeStatus.PAGADO) {
      return [
        {
          label: "Ver detalle",
          command: () => this.detailCharge(charge),
        },
      ];
    } else if (charge.status === ChargeStatus.ANULADO) {
      return [];
    } else if (charge.status === ChargeStatus.NOPAGADO) {
      //charge.status === ChargeStatus.PENDIENTE
      return [
        {
          label: "Modificar",
          command: () => this.modifyChage(charge),
        },
        {
          separator: true,
        },
        {
          label: "Reintentar",
          command: () => this.retryCharge(charge),
        },
        // {
        //   separator: true,
        // },
        // {
        //   label: "Anular",
        //   command: () => this.cancelCharge(charge),
        // },
      ];
    } else {
      return [
        {
          label: "Modificar",
          command: () => this.modifyChage(charge),
        },
      ];
    }
  }

  retryCharge(charge: Charge) {
    console.log("retryCharge charge", charge);
    this.selectedCharge.set(charge);
    this.showConfirmChargeDialog.set(true);
  }

  modifyChage(charge: Charge) {
    console.log("modifyChage charge", charge);
    this.selectedCharge.set(charge);
    this.showNewChargeDialog.set(true);
  }

  cancelCharge(charge: Charge) {
    const authToken = localStorage.getItem("auth.access_token") || "";
    const merchantId = localStorage.getItem("auth.merchant_id") || "";

    this.onlineCheckoutService.cancelCheckout(charge.id, merchantId, authToken).subscribe({
      next: (response) => {
        console.log("Charge canceled successfully:", response);
        this.loadCharges();
      },
      error: (error) => {
        console.error("Error canceling charge:", error);
      },
    });
  }

  detailCharge(charge: Charge) {
    this.showDetailChargeDialog.set(true);
    console.log("detailCharge charge", charge);
    this.selectedCharge.set(charge);
  }

  closeDetailDialog(): void {
    this.showDetailChargeDialog.set(false);
    this.selectedCharge.set(null);
  }

  closeConfirmChargeDialog(): void {
    this.showConfirmChargeDialog.set(false);
    this.showNewChargeDialog.set(true);
  }

  getVariant(charge: Charge) {
    return getBadgeVariant(charge);
  }

  exportData(): void {
    console.log("Exportar datos");
    // TODO: Implementar lógica de exportación
  }

  openFilterDialog(): void {
    this.visible = true;
    if (!this.form) {
      this.initializeForm();
    }
  }

  private initializeForm(): void {
    this.availableTypes = [...new Set(this.charges().map((c) => c.type))];

    this.form = new FormGroup({
      dateRange: new FormControl("all"),
      from: new FormControl(null),
      to: new FormControl(null),
      types: new FormControl([...this.availableTypes]),
      statuses: new FormControl([...this.statusOptions]),
    });

    this.form.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.applyAllFilters();
    });
  }

  private applyAllFilters(): void {
    let filtered = [...this.charges()];
    const searchTerm = this.searchTerm();
    const formValue = this.form?.value;

    // Filtro de búsqueda
    if (searchTerm) {
      filtered = filtered.filter((charge) => charge.id.toLowerCase().includes(searchTerm.toLowerCase()) || charge.type.toLowerCase().includes(searchTerm.toLowerCase()));
    }

    // Filtros del formulario
    if (formValue) {
      // Filtro de fecha
      if (formValue.dateRange !== "all") {
        const { start, end } = this.getDateRange(formValue.dateRange, formValue.from, formValue.to);
        if (start && end) {
          filtered = filtered.filter((charge) => {
            const chargeDate = new Date(charge.date);
            return chargeDate >= start && chargeDate <= end;
          });
        }
      }

      // Filtro de tipos (solo si hay tipos seleccionados)
      if (formValue.types?.length > 0 && formValue.types.length < this.availableTypes.length) {
        filtered = filtered.filter((charge) => formValue.types.includes(charge.type));
      }

      // Filtro de estados (solo si hay estados seleccionados)
      if (formValue.statuses?.length > 0 && formValue.statuses.length < this.statusOptions.length) {
        filtered = filtered.filter((charge) => formValue.statuses.includes(charge.status));
      }
    }

    this.filteredCharges.set(filtered);
    this.applySorting();
  }

  private applySorting(): void {
    if (!this.sortField || !this.sortDirection) {
      return;
    }

    const sorted = [...this.filteredCharges()].sort((a, b) => {
      const aVal = a[this.sortField!];
      const bVal = b[this.sortField!];

      if (this.sortField === 'date') {
        const aTime = new Date(aVal as string).getTime();
        const bTime = new Date(bVal as string).getTime();
        return this.sortDirection === SortDirection.ASC ? aTime - bTime : bTime - aTime;
      }

      if (this.sortField === 'amount') {
        return this.sortDirection === SortDirection.ASC ? (aVal as number) - (bVal as number) : (bVal as number) - (aVal as number);
      }

      const aStr = String(aVal).toLowerCase();
      const bStr = String(bVal).toLowerCase();
      return this.sortDirection === SortDirection.ASC ? aStr.localeCompare(bStr) : bStr.localeCompare(aStr);
    });

    this.filteredCharges.set(sorted);
  }

  private getDateRange(range: string, from: Date | null, to: Date | null): { start: Date | null; end: Date | null } {
    const now = new Date();

    switch (range) {
      case "today":
        return {
          start: this.startOfDay(now),
          end: this.endOfDay(now),
        };
      case "yesterday":
        const yesterday = new Date(now);
        yesterday.setDate(yesterday.getDate() - 1);
        return {
          start: this.startOfDay(yesterday),
          end: this.endOfDay(yesterday),
        };
      case "last7days":
        const last7 = new Date(now);
        last7.setDate(last7.getDate() - 7);
        return {
          start: this.startOfDay(last7),
          end: this.endOfDay(now),
        };
      case "last30days":
        const last30 = new Date(now);
        last30.setDate(last30.getDate() - 30);
        return {
          start: this.startOfDay(last30),
          end: this.endOfDay(now),
        };
      case "custom":
        return {
          start: from ? this.startOfDay(from) : null,
          end: to ? this.endOfDay(to) : null,
        };
      default:
        return { start: null, end: null };
    }
  }

  private startOfDay(date: Date): Date {
    const result = new Date(date);
    result.setHours(0, 0, 0, 0);
    return result;
  }

  private endOfDay(date: Date): Date {
    const result = new Date(date);
    result.setHours(23, 59, 59, 999);
    return result;
  }

  clearFilters(): void {
    this.form.patchValue({
      dateRange: "all",
      from: null,
      to: null,
      types: [...this.availableTypes],
      statuses: [...this.statusOptions],
    });
  }

  onTypeToggle(type: string, checked: boolean): void {
    const types = this.form.get("types")?.value || [];
    if (checked) {
      this.form.patchValue({ types: [...types, type] });
    } else {
      this.form.patchValue({ types: types.filter((t: string) => t !== type) });
    }
  }

  onStatusToggle(status: ChargeStatus, checked: boolean): void {
    const statuses = this.form.get("statuses")?.value || [];
    if (checked) {
      this.form.patchValue({ statuses: [...statuses, status] });
    } else {
      this.form.patchValue({ statuses: statuses.filter((s: ChargeStatus) => s !== status) });
    }
  }

  toggleAllTypes(checked: boolean): void {
    this.form.patchValue({ types: checked ? [...this.availableTypes] : [] });
  }

  toggleAllStatuses(checked: boolean): void {
    this.form.patchValue({ statuses: checked ? [...this.statusOptions] : [] });
  }

  isTypeSelected(type: string): boolean {
    return this.form?.get("types")?.value?.includes(type) || false;
  }

  isStatusSelected(status: ChargeStatus): boolean {
    return this.form?.get("statuses")?.value?.includes(status) || false;
  }

  areAllTypesSelected(): boolean {
    const selectedTypes = this.form?.get("types")?.value || [];
    return selectedTypes.length === this.availableTypes.length && this.availableTypes.length > 0;
  }

  areAllStatusesSelected(): boolean {
    const selectedStatuses = this.form?.get("statuses")?.value || [];
    return selectedStatuses.length === this.statusOptions.length && this.statusOptions.length > 0;
  }
}
